<template>
  <div class="TSN-page" :class="{ 'is-english': isEnglish }">
    <SectionMask />

    <main class="product-content">
    <section class="product-hero">
      <picture>
        <source media="(max-width: 768px)" :srcset="heroPhone" />
        <img :src="heroWeb" alt="TSK SELECT Needle True 34G" />
      </picture>
      <div class="product-hero-copy">
        <h1>TSK SELECT<sup>™</sup><br />Needle</h1>
        <p class="product-hero-name" v-html="$t('TskSelectNeedle.public.heroName')"></p>
        <p class="product-hero-gauge" v-html="$t('TskSelectNeedle.public.heroGauge')"></p>
        <p class="product-hero-license" v-html="$t('TskSelectNeedle.public.heroLicense')"></p>
      </div>
    </section>

    <section class="intro-copy section-copy">
      <h2 v-html="$t('TskSelectNeedle.public.introTitle')"></h2>
      <p v-html="$t('TskSelectNeedle.public.introBody')"></p>
      <strong v-html="$t('TskSelectNeedle.public.introLead')"></strong>
    </section>

    <section class="needle-overview">
      <picture>
        <source media="(max-width: 899px)" srcset="/TSN_NEW/TSN_needle_phone.png" />
        <img src="/TSN_NEW/TSN_needle.png" alt="TSK SELECT 34G needle" loading="lazy" />
      </picture>
      <div class="needle-overview-copy">
        <h2 v-html="$t('TskSelectNeedle.public.overviewTitle')"></h2>
        <h3 v-html="$t('TskSelectNeedle.public.overviewSubtitle')"></h3>
        <div class="needle-overview-details">
          <p v-html="$t('TskSelectNeedle.public.overviewBody1')"></p>
          <p v-html="$t('TskSelectNeedle.public.overviewBody2')"></p>
        </div>
      </div>
    </section>

    <section class="two-mm-section content-width">
      <img class="two-mm-product" src="/TSN_NEW/TSN_2mm.png" alt="TSK 2mm Needle" loading="lazy" />
      <div class="two-mm-copy">
        <h2 v-html="$t('TskSelectNeedle.public.twoMmTitle')"></h2>
        <ul>
          <li v-html="$t('TskSelectNeedle.public.twoMmPoint1')"></li>
          <li v-html="$t('TskSelectNeedle.public.twoMmPoint2')"></li>
          <li v-html="$t('TskSelectNeedle.public.twoMmPoint3')"></li>
        </ul>
        <div class="two-mm-visual">
          <div class="two-mm-icons">
            <figure>
              <img src="/TSN_NEW/TSN_2mm_icon_1.png" :alt="$t('TskSelectNeedle.public.twoMmIcon1')" loading="lazy" />
              <figcaption v-html="$t('TskSelectNeedle.public.twoMmIcon1')"></figcaption>
            </figure>
            <figure>
              <img src="/TSN_NEW/TSN_2mm_icon_2.png" :alt="$t('TskSelectNeedle.public.twoMmIcon2')" loading="lazy" />
              <figcaption v-html="$t('TskSelectNeedle.public.twoMmIcon2')"></figcaption>
            </figure>
            <figure>
              <img src="/TSN_NEW/TSN_2mm_icon_3.png" :alt="$t('TskSelectNeedle.public.twoMmIcon3')" loading="lazy" />
              <figcaption v-html="$t('TskSelectNeedle.public.twoMmIcon3')"></figcaption>
            </figure>
          </div>
          <picture class="two-mm-diagram-picture">
            <source media="(max-width: 899px)" srcset="/TSN_NEW/TSN_2mm_1_phone.png" />
            <img class="two-mm-diagram" src="/TSN_NEW/TSN_2mm_1.png" alt="2mm injection depth comparison" loading="lazy" />
          </picture>
        </div>
        <p class="two-mm-note" v-html="$t('TskSelectNeedle.public.twoMmNote')"></p>
      </div>
    </section>

    <section class="quality-section section-copy">
      <h2 v-html="$t('TskSelectNeedle.AppShell.s6.title')"></h2>
      <p v-html="qualityBodyWithMobileBreaks"></p>
      <Durable class="quality-mobile-animation" />
      <video autoplay muted loop playsinline preload="metadata" aria-label="TSK SELECT needle durability comparison">
        <source src="/TSN_NEW/Needle..mp4" type="video/mp4" />
      </video>
    </section>

    <ThinWall />

    <section class="lds-section">
      <div class="lds-static">
        <img src="/TSN_NEW/TSN_LDS.png" alt="TSK SELECT low dead space comparison" loading="lazy" />
      </div>
      <div class="lds-copy">
        <h2 v-html="$t('TskSelectNeedle.public.ldsTitle')"></h2>
        <p v-html="$t('TskSelectNeedle.public.ldsBody')"></p>
        <a href="/product/TSKSyringe" v-html="$t('TskSelectNeedle.public.ldsLink')"></a>
      </div>
    </section>

    <section class="feature-section content-width">
      <div class="feature-icons">
        <figure v-for="(feature, index) in features" :key="feature.label">
          <img :src="`/TSN_NEW/TSN_icon_${index + 1}.png`" :alt="$t(feature.label)" loading="lazy" />
          <figcaption
            :class="{ 'feature-label--single': isSingleLineFeature(feature) }"
          >{{ $t(feature.label) }}</figcaption>
        </figure>
      </div>
      <h2 v-html="$t('TskSelectNeedle.public.featureTitle')"></h2>
      <div class="feature-list">
        <ul>
          <li v-for="key in ['feature1', 'feature2', 'feature3', 'feature4']" :key="key" v-html="$t(`TskSelectNeedle.public.${key}`)"></li>
        </ul>
        <ul>
          <li v-for="key in ['feature5', 'feature6', 'feature7', 'feature8']" :key="key" v-html="$t(`TskSelectNeedle.public.${key}`)"></li>
        </ul>
      </div>
    </section>

    <section class="video-section content-width">
      <iframe
        src="https://www.youtube.com/embed/cs1eOKwq3PQ"
        title="TSK Select Needle video"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
      ></iframe>
    </section>

    <ProductSpec />
    </main>
    <ContactFooter />
  </div>
</template>

<script>
import SectionMask from './TskSelectNeedle/SectionMask.vue'
import Durable from './TskSelectNeedle/Durable.vue'
import ThinWall from './TskSelectNeedle/ThinWall.vue'
import ProductSpec from './TskSelectNeedle/ProductSpec.vue'
import ContactFooter from '@/components/ContactFooter.vue'

export default {
  name: 'TskSelectNeedle',
  components: { SectionMask, Durable, ThinWall, ProductSpec, ContactFooter },
  data() {
    return {
      features: [
        { label: 'TskSelectNeedle.public.icon1' },
        { label: 'TskSelectNeedle.public.icon2' },
        { label: 'TskSelectNeedle.public.icon3' },
        { label: 'TskSelectNeedle.public.icon4' },
        { label: 'TskSelectNeedle.public.icon5' },
        { label: 'TskSelectNeedle.public.icon6' },
        { label: 'TskSelectNeedle.public.icon7' },
        { label: 'TskSelectNeedle.public.icon8' }
      ]
    }
  },
  computed: {
    isEnglish() {
      return String(this.$i18n.locale || '').toLowerCase().startsWith('en')
    },
    heroWeb() {
      return require('@/assets/images/AppShell/products/TSN/web/TSN_01.png')
    },
    heroPhone() {
      return require('@/assets/images/AppShell/products/TSN/phone/TSN_phone_01.png')
    },
    qualityBodyWithMobileBreaks() {
      return String(this.$t('TskSelectNeedle.AppShell.s6.body'))
        .replace('針頭研磨、', '針頭研磨、<span class="quality-mobile-line-break"></span>')
        .replace('全程嚴格控管，', '全程嚴格控管，<span class="quality-mobile-line-break"></span>')
    }
  },
  methods: {
    isSingleLineFeature(feature) {
      return !String(this.$t(feature.label)).includes('\n')
    }
  }
}
</script>

<style scoped>
.TSN-page {
  --accent: #5fa4b8;
  position: relative;
  overflow-x: hidden;
  color: #555;
  background: #fff;
}

.product-content {
  width: min(calc(100% - 3rem), 1320px);
  margin: 0 auto;
  overflow: visible;
  font-size: 16px;
}

.content-width {
  width: min(calc(100% - 3rem), 1320px);
  margin-inline: auto;
}

.product-hero img,
.needle-overview img {
  display: block;
  width: 100%;
  height: auto;
}

.product-hero {
  position: relative;
}

.product-hero-copy {
  position: absolute;
  top: 50%;
  left: 17%;
  width: min(34%, 390px);
  transform: translateY(-50%);
  color: #222;
  text-align: left;
}

.product-hero-copy h1 {
  margin: 0 0 0.75rem;
  color: #111 !important;
  -webkit-text-fill-color: #111;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.02em;
}

.product-hero-copy h1 sup {
  position: relative;
  top: -0.38em;
  margin-left: 0.12em;
  font-size: 0.65em;
  line-height: 0;
}

.product-hero-name {
  margin: 0 0 0.8rem;
  color: #222;
  font-size: 20px;
  line-height: 1.45;
}

.product-hero-gauge {
  display: inline-block;
  margin: 0;
  padding: 0.12em 0.35em;
  border-radius: 8px;
  background: linear-gradient(90deg, #32c5bf 0%, #5b9dea 100%);
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
}

.product-hero-license {
  margin: 1.25rem 0 0;
  color: #222;
  font-size: 16px;
  line-height: 1.9;
}

.section-copy {
  width: min(calc(100% - 3rem), 1320px);
  margin: 0 auto;
  padding: 4rem 0;
  text-align: center;
  line-height: 1.9;
}

.intro-copy {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f7f7f7;
  text-align: center;
}

.intro-copy > h2,
.intro-copy > p,
.intro-copy > strong {
  display: block;
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  text-align: center !important;
}

:deep(.mobile-line-break) {
  display: none;
}

.section-copy h2,
.two-mm-copy h2,
.lds-copy h2 {
  margin: 0 0 12px;
  color: var(--accent);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
}

.section-copy p {
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.9;
}

.needle-overview {
  position: relative;
  background: #eef4f8;
}

.needle-overview-copy {
  position: absolute;
  top: 18%;
  left: 50%;
  width: min(58%, 760px);
  transform: translateX(-50%);
  text-align: center;
}

.needle-overview-copy h2 {
  margin: 0 0 1rem;
  color: var(--accent);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
}

.needle-overview-copy h3 {
  margin: 0;
  color: #555555 !important;
  -webkit-text-fill-color: #555555;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.9;
}

.needle-overview-details {
  margin-top: 2.5rem;
}

.needle-overview-details p {
  margin: 0;
  color: #555;
  font-size: 16px;
  line-height: 1.9;
}

.two-mm-section {
  display: grid;
  grid-template-columns: minmax(260px, 31%) minmax(0, 1fr);
  gap: 48px;
  align-items: end;
  padding: 70px 0 40px;
}

.two-mm-product,
.two-mm-diagram {
  display: block;
  width: 100%;
  height: auto;
}

.two-mm-diagram-picture {
  display: block;
}

.two-mm-copy {
  min-width: 0;
  font-size: 16px;
  line-height: 1.9;
  text-align: left;
}

.two-mm-copy ul {
  margin: 18px 0 28px;
  padding-left: 1.4em;
  text-align: left;
}

.two-mm-visual {
  position: relative;
}

.two-mm-icons {
  position: absolute;
  z-index: 1;
  top: 8%;
  left: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  width: min(56%, 500px);
  margin: 0;
  transform: scale(0.9);
  transform-origin: top left;
}

.two-mm-icons figure {
  margin: 0;
  color: var(--accent);
  text-align: center;
}

.two-mm-icons img {
  display: block;
  width: min(100%, 96px);
  height: auto;
  margin: 0 auto 8px;
}

.two-mm-icons figcaption {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.45;
}

.two-mm-note {
  margin: 10px 0 0;
  color: #555;
  font-size: 14px;
  line-height: 1.7;
  text-align: center;
}

.quality-section {
  position: relative;
  padding: 70px 0 0;
}

.quality-section > h2,
.quality-section > p {
  position: relative;
  z-index: 2;
}

.quality-section :deep(.quality-mobile-line-break) {
  display: none;
}

.quality-mobile-animation {
  display: none;
}

.quality-section video {
  position: relative;
  z-index: 1;
  display: block;
  width: min(1100px, 100%);
  margin: -100px auto 0;
}

.lds-section {
  display: grid;
  grid-template-columns: 1fr 1.55fr;
  margin-top: 48px;
  background: #f4fde9 url('/TSN_NEW/TSN_LDS_bg.png') center / cover no-repeat;
  min-height: 460px;
  align-items: stretch;
}

.lds-static {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  overflow: hidden;
}

.lds-static img {
  display: block;
  width: min(100%, 620px);
  height: auto;
}

.lds-copy {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0 7%;
  overflow: hidden;
  text-align: left;
}

.lds-copy h2 {
  margin-bottom: 24px;
  line-height: 1.55;
  text-align: left;
}

.lds-copy p {
  max-width: 650px;
  margin: 0 0 28px;
  font-size: 16px;
  line-height: 1.9;
  text-align: left;
}

.lds-copy > a {
  color: var(--accent);
  font-weight: 700;
  line-height: 1.7;
  text-align: left;
  text-decoration: none;
}

.feature-section {
  padding: 58px 0 72px;
}

.feature-icons {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  align-items: end;
  margin-bottom: 28px;
}

.feature-icons figure {
  position: relative;
  margin: 0;
}

.feature-icons img {
  display: block;
  width: 100%;
}

.feature-icons figcaption {
  position: absolute;
  right: 4px;
  bottom: 11%;
  left: 4px;
  color: var(--accent);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  white-space: pre-line;
}

.feature-section > h2 {
  margin: 0;
  padding: 14px 24px;
  color: #fff !important;
  -webkit-text-fill-color: #fff;
  background: var(--accent);
  text-align: center;
  font-size: 24px;
  line-height: 1.45;
}

.feature-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 70px;
  padding: 26px 9%;
  font-size: 16px;
  line-height: 1.9;
  text-align: left;
}

.feature-list ul {
  margin: 0;
  padding-left: 1.25em;
}

.feature-list li {
  margin: 8px 0;
  padding-left: 0.15em;
}

.video-section {
  padding: 24px 0 70px;
}

.video-section iframe {
  display: block;
  width: min(100%, 980px);
  aspect-ratio: 16 / 9;
  margin: 0 auto;
}

.product-content :deep(.ThinWall-title) {
  font-size: 24px;
}

@media (max-width: 899px), (orientation: portrait) {
  .product-content {
    width: 100%;
  }

  .product-hero-copy {
    top: 5%;
    left: 50%;
    width: min(88%, 420px);
    transform: translateX(-50%);
    text-align: center;
  }

  .product-hero-copy h1 {
    font-size: 24px;
  }

  .product-hero-name {
    font-size: 18px;
  }

  .product-hero-license {
    margin-top: 0.35rem;
  }

  .section-copy {
    width: calc(100% - 2rem);
    padding: 30px 0;
  }

  .intro-copy {
    width: 100%;
    background: #fff;
  }

  .needle-overview {
    background: #fff;
  }

  :deep(.mobile-line-break) {
    display: block;
  }

  .content-width {
    width: calc(100% - 2rem);
  }

  .needle-overview-copy {
    position: absolute;
    top: 58%;
    left: 50%;
    width: calc(100% - 2rem);
    padding: 0;
    transform: translateX(-50%);
  }

  .TSN-page.is-english .needle-overview {
    box-sizing: border-box;
    padding: 96vw 1rem 24px;
    overflow: hidden;
  }

  .TSN-page.is-english .needle-overview picture {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
  }

  .TSN-page.is-english .needle-overview-copy {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    transform: none;
  }

  .TSN-page.is-english .needle-overview-copy h2 {
    margin-bottom: 0.4rem;
    line-height: 1.3;
  }

  .TSN-page.is-english .needle-overview-copy h3 {
    line-height: 1.55;
  }

  .TSN-page.is-english .needle-overview-details {
    margin-top: 0.5rem;
  }

  .TSN-page.is-english .needle-overview-details p {
    line-height: 1.55;
  }

  .needle-overview-copy h2 {
    margin-bottom: 0.65rem;
    font-size: 24px;
    line-height: 1.45;
  }

  .needle-overview-copy h3 {
    font-size: 16px;
    line-height: 1.9;
  }

  .needle-overview-details {
    margin-top: 0.75rem;
  }

  .needle-overview-details p {
    font-size: 16px;
    line-height: 1.9;
  }

  .two-mm-section,
  .lds-section {
    grid-template-columns: 1fr;
  }

  .two-mm-section {
    gap: 24px;
    padding: 42px 0;
  }

  .two-mm-product {
    width: min(74%, 380px);
    margin: 0 auto;
  }

  .two-mm-copy,
  .two-mm-copy h2 {
    text-align: center;
  }

  .two-mm-copy ul {
    display: inline-block;
    margin-right: auto;
    margin-left: auto;
    padding-left: 1.4em;
    list-style-position: outside;
    text-align: left;
  }

  .two-mm-icons {
    position: static;
    width: 100%;
    gap: 10px;
    margin: 20px 0 24px;
    transform: none;
  }

  .two-mm-icons img {
    width: min(100%, 76px);
  }

  .two-mm-icons figcaption,
  .two-mm-note {
    font-size: 14px;
  }

  .quality-section video {
    display: none;
  }

  .quality-mobile-animation {
    display: block;
    width: 100%;
    aspect-ratio: 1080 / 1320;
    margin: 0;
    overflow: hidden;
  }

  .quality-mobile-animation :deep(.Durable-animation) {
    transform: translateY(-28%);
  }

  .quality-mobile-animation :deep(.Durable-title),
  .quality-mobile-animation :deep(.Durable-black-bar) {
    display: none;
  }

  .quality-section {
    width: 100%;
    padding: 36px 0 0;
  }

  .quality-section > h2,
  .quality-section > p {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .quality-section :deep(.quality-mobile-line-break) {
    display: block;
  }

  .lds-static {
    justify-content: center;
  }

  .lds-copy {
    order: -1;
    align-items: center;
    padding: 34px 7% 40px;
    text-align: center;
  }

  .lds-section {
    margin-top: 28px;
  }

  .lds-copy h2,
  .lds-copy p,
  .lds-copy > a {
    text-align: center;
  }

  .lds-copy p br {
    display: none;
  }

  .feature-icons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 10px;
    width: 80%;
    margin-right: auto;
    margin-left: auto;
  }

  .feature-icons figure {
    display: flex;
    justify-content: center;
  }

  .feature-icons img {
    width: 87.5%;
    margin: 0 auto;
  }

  .feature-icons figcaption {
    right: 6.25%;
    bottom: 8%;
    left: 6.25%;
    font-size: clamp(12px, 3.4vw, 15px);
    line-height: 1.25;
  }

  .feature-icons figcaption.feature-label--single {
    bottom: 14%;
  }

  .feature-list {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 20px 5%;
  }
}
</style>
